import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { EBanqupRequestStatus } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Button,
  Icon,
  MainCard,
  SpinningLoader,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentStatusStyles } from "./PaymentStatus.styles";

let fetchDataInterval: NodeJS.Timeout | undefined;

export const PaymentStatus = () => {
  const navigate = useNavigate();
  const params = useParams();

  const sx = PaymentStatusStyles();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const {
    detail: paymentRequest,
    refetch,
    isFetching,
  } = paymentRequestHooks.useDetail({
    shouldRefetch: false,
    id: paymentRequestId,
  });

  const fetchData = React.useCallback(() => {
    if (!paymentRequestId) {
      return;
    }

    if (isFetching) {
      return;
    }

    if (
      paymentRequest?.banqupRequestStatus &&
      [
        EBanqupRequestStatus.Accepted,
        EBanqupRequestStatus.Rejected,
        EBanqupRequestStatus.Terminated,
        EBanqupRequestStatus.AutoApproved,
      ].includes(paymentRequest?.banqupRequestStatus)
    ) {
      return;
    }

    // Only fetch new data for new or approved payment request
    if (
      paymentRequest &&
      paymentRequest.status !== EPaymentRequestStatus.New &&
      paymentRequest.status !== EPaymentRequestStatus.Approved
    ) {
      return;
    }

    refetch();
  }, [paymentRequest]);

  // Lifecycle
  React.useEffect(() => {
    fetchDataInterval = setInterval(fetchData, 2500);

    return () => {
      if (fetchDataInterval) {
        clearInterval(fetchDataInterval);
      }
    };
  }, [paymentRequest]);

  const getMangoPayColor = (status: EPaymentRequestStatus) => {
    switch (status) {
      case EPaymentRequestStatus.Paid:
        return "#1ab394";
      case EPaymentRequestStatus.Failed:
        return "#d54c48";
      default:
        return "#ec9c3d";
    }
  };

  const renderMangoPayStatus = (
    status: EPaymentRequestStatus,
    title: string,
    info?: string,
  ) => {
    return (
      <>
        <Icon
          path={EAssets.PaymentSuccess}
          realColor={getMangoPayColor(status)}
          width="188px"
          height="188px"
        />
        <Typography variant="h2" mt={12.5}>
          {title}
        </Typography>
        {info && (
          <Typography variant="body1" mt={5} sx={sx.content.info}>
            {info}
          </Typography>
        )}
      </>
    );
  };

  const renderMangoPayPaymentStatus = () => {
    switch (paymentRequest?.status) {
      case EPaymentRequestStatus.New:
      case EPaymentRequestStatus.Approved:
        return renderMangoPayStatus(
          EPaymentRequestStatus.New,
          "Payment is pending",
        );

      // All 'success' states
      case EPaymentRequestStatus.Pending:
      case EPaymentRequestStatus.PaidIn:
      case EPaymentRequestStatus.Partial:
      case EPaymentRequestStatus.Transferring:
      case EPaymentRequestStatus.Transferred:
      case EPaymentRequestStatus.PayingOut:
      case EPaymentRequestStatus.Paid: {
        const postfix = paymentRequest.paymentMethod;

        const sepaBankDaysOffset =
          paymentRequestUtils.sepaDeductionDaysFromNow(paymentRequest);

        const titleKeyBase = "payment.feedback.success.title";
        let titleKey = postfix ? `${titleKeyBase}.${postfix}` : titleKeyBase;
        // Untranslated for payment method => use base key
        if (titleKey === getLocalizedText(titleKey)) {
          titleKey = titleKeyBase;
        }
        const title = getLocalizedText(titleKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        const infoKeyBase = "payment.feedback.success.info";
        let infoKey = postfix ? `${infoKeyBase}.${postfix}` : infoKeyBase;
        // Untranslated for payment method => use base key
        if (infoKey === getLocalizedText(infoKey)) {
          infoKey = infoKeyBase;
        }
        const info = getLocalizedText(infoKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        return renderMangoPayStatus(EPaymentRequestStatus.Paid, title, info);
      }

      case EPaymentRequestStatus.FailedTransfer:
      case EPaymentRequestStatus.FailedPaidIn:
      case EPaymentRequestStatus.FailedPayout:
      case EPaymentRequestStatus.FailedKyc:
      case EPaymentRequestStatus.Failed:
        return renderMangoPayStatus(
          EPaymentRequestStatus.Failed,
          getLocalizedText("payment.feedback.error.title"),
          getLocalizedText("payment.feedback.error.info"),
        );

      default:
        return <></>;
    }
  };

  const getBanqupPayColor = (status: EBanqupRequestStatus) => {
    switch (status) {
      case EBanqupRequestStatus.Accepted:
        return "#1ab394";
      case EBanqupRequestStatus.Rejected:
        return "#d54c48";
      default:
        return "#ec9c3d";
    }
  };

  const renderBanqupStatus = (
    status: EBanqupRequestStatus,
    title: string,
    info?: string,
  ) => {
    return (
      <>
        <Icon
          path={EAssets.PaymentSuccess}
          realColor={getBanqupPayColor(status)}
          width="188px"
          height="188px"
        />
        <Typography variant="h2" mt={12.5}>
          {title}
        </Typography>
        {info && (
          <Typography variant="body1" mt={5} sx={sx.content.info}>
            {info}
          </Typography>
        )}
      </>
    );
  };

  const renderBanqupPaymentStatus = () => {
    switch (paymentRequest?.banqupRequestStatus) {
      case EBanqupRequestStatus.InReview:
        return renderBanqupStatus(
          EBanqupRequestStatus.InReview,
          getLocalizedText("payment.feedback.pending.title"),
          getLocalizedText("payment.feedback.pending.info"),
        );

      case EBanqupRequestStatus.Validated:
      case EBanqupRequestStatus.Submitted:
      case EBanqupRequestStatus.Accepted:
      case EBanqupRequestStatus.AutoApproved:
        return renderBanqupStatus(
          EBanqupRequestStatus.Accepted,
          getLocalizedText("payment.feedback.success.title"),
          getLocalizedText("payment.feedback.success.info"),
        );

      case EBanqupRequestStatus.Rejected:
      case EBanqupRequestStatus.Terminated:
        return renderBanqupStatus(
          EBanqupRequestStatus.Rejected,
          getLocalizedText("payment.feedback.error.title"),
          getLocalizedText("payment.feedback.error.info"),
        );

      default:
        return <></>;
    }
  };

  const handleRenderPaymentStatus = () => {
    if (paymentRequest?.banqupRequestStatus) {
      return renderBanqupPaymentStatus();
    }

    return renderMangoPayPaymentStatus();
  };

  return (
    <MainCard contentSX={sx.wrapper}>
      {isFetching ? (
        <SpinningLoader />
      ) : (
        <>
          {handleRenderPaymentStatus()}
          <Button
            variant={EButtonVariants.Contained}
            onClick={() => navigate("/payments")}
            sx={sx.content.close}
          >
            {getLocalizedText("system.close")}
          </Button>
        </>
      )}
    </MainCard>
  );
};
